import { Box, Button, Container, Typography, styled, useTheme } from '@mui/material'
import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet'
import useAppUtilities from '../../../hooks/useAppUtilities'
import { ROUTES } from '../../../helpers/routes-helper'
import { MuiAlert, MuiSnackbar } from '../styled/global.styled'
import { useAppSelector } from '../../../store'

const SuccessPageWrapper = styled('div')({
  backgroundSize: '60%,40%',
  backgroundPosition: 'top 15px left 0px, top 0px right 0px',
  backgroundImage: `url('/images/about/background_1.png'), url('/images/about/background_2.png')`,
  backgroundRepeat: 'no-repeat',
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
})

const SuccessCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '24px',
  padding: theme.spacing(6),
  boxShadow: '0px 2px 12px rgba(0, 0, 0, 0.06)',
  maxWidth: '600px',
  margin: '0 auto',
  textAlign: 'center',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(4),
  },
}))

const SuccessButton = styled(Button)(({ theme }) => ({
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '16px',
  padding: theme.spacing(1.5, 3),
  borderRadius: '12px',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
  [theme.breakpoints.up('sm')]: {
    minWidth: '200px',
  },
}))

const ButtonGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    gap: theme.spacing(2),
    justifyContent: 'center',
  },
}))

const DocumentTitle = 'Resactly | Subscription Successful'

const SubscriptionSuccessPage: React.FC = () => {
  const { navigate } = useAppUtilities()
  const theme = useTheme()
  const message = useAppSelector((state) => state.profile.message)
  const severity = useAppSelector((state) => state.profile.severity)
  const openSnackbar = useAppSelector((state) => state.profile.shouldOpen)

  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo(0, 0)
  }, [])

  const handleGoToWallet = (): void => {
    navigate(`${ROUTES.PROFILE}?tab=Wallet`)
  }

  const handleViewPoints = (): void => {
    navigate(ROUTES.POINTS)
  }

  return (
    <SuccessPageWrapper>
      <Helmet>
        <title>{DocumentTitle}</title>
        <meta
          name="description"
          content="Your subscription to Resactly is now active. Start redeeming your points for rewards!"
        />
      </Helmet>

      <Container component="main" maxWidth="lg" sx={{ flex: 1, py: { xs: 3, md: 5 } }}>
        <SuccessCard>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              color: theme.palette.grey[800],
              fontWeight: 700,
              marginBottom: 2,
              fontSize: { xs: '28px', sm: '40px' },
              lineHeight: { xs: '36px', sm: '52px' },
            }}
          >
            Subscription successful 🎉
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.grey[700],
              fontWeight: 500,
              marginBottom: 4,
              lineHeight: 1.6,
              fontSize: { xs: '16px', sm: '18px' },
            }}
          >
            Your subscription is active. Your points and rewards will update automatically as you use Resactly.
          </Typography>
          <ButtonGroup>
            <SuccessButton
              variant="contained"
              size="large"
              onClick={handleGoToWallet}
            >
              Go to Wallet
            </SuccessButton>
            <SuccessButton
              variant="outlined"
              size="large"
              onClick={handleViewPoints}
            >
              View Points
            </SuccessButton>
          </ButtonGroup>
        </SuccessCard>
      </Container>

      <MuiSnackbar
        open={openSnackbar}
        onClose={() => {
          // Handle close if needed
        }}
      >
        <MuiAlert
          severity={severity ?? 'success'}
        >
          {message}
        </MuiAlert>
      </MuiSnackbar>
    </SuccessPageWrapper>
  )
}

export default SubscriptionSuccessPage
